import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Stack, IconButton } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OutsideClickHandler from "react-outside-click-handler";
import ProfileMenu from "../../components/ProfileMenu";
import { Images } from "../../assets/common";
import ApiService from "../../services/api-service";
import { HMS } from "../../constants/function";
import { useDispatch } from "react-redux";
import { addSongsAction } from "../../action/songs";
import { useTranslation } from "react-i18next";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import { useHistory } from "react-router-dom";

export default function MyMusicTab({ userId }) {
  const [myMusic, setMyMusic] = useState([]);
  const [loadingMusic, setLoadingMusic] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [open, setOpen] = useState(false);
  const [showStudioPopup, setShowStudioPopup] = useState(false);
  const [studioMusicId, setStudioMusicId] = useState(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    setLoadingMusic(true);
    ApiService.MY_MUSIC(`assetId=${userId}&type=user`)
      .then(res => setMyMusic(res.result || []))
      .finally(() => setLoadingMusic(false));
  }, [userId]);

  // Menu actions for each row
  const getMenuActions = (m) => [
    { key: "info", action: "View Song Information" },
    { key: "add_to_studio", action: "Add to Studio" },
    {
      key: m.private ? "mark_public" : "mark_private",
      action: m.private ? "Mark Public" : "Mark Private"
    }
  ];

 const handleMenuAction = async (key, musicId, idx) => {
  setOpen(false);
  setSelectedIndex(null);

  if (key === "info") {
    // If NFT ID exists, go to NFT details, else go to song details
    const nftId = myMusic[idx]?.nftId;
    if (nftId) {
      history.push(`/app/NftDetail/${nftId}`);
    } else {
      history.push(`/app/SongDetail/${musicId}`);
    }
  } else if (key === "add_to_studio") {
    setStudioMusicId(musicId);
    setShowStudioPopup(true);
  } else if (key === "mark_private" || key === "mark_public") {
    try {
      setLoadingMusic(true);
      await ApiService.ASSET_PRIVACY({
        assetId: musicId,
        type: "music",
        private: !myMusic[idx].private,
      });
      const updated = [...myMusic];
      updated[idx].private = !updated[idx].private;
      setMyMusic(updated);
      setLoadingMusic(false);
    } catch (error) {
      setLoadingMusic(false);
      alert("Failed to update privacy");
    }
  }
};
  return (
    <>
      <Grid container spacing={2} sx={{
        color: "#fff",
        mb: 1,
        px: 1,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        fontSize: 15,
        borderBottom: "1px solid #232323",
        pb: 1.5,
      }}>
        <Grid item xs={4}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#fff" }}>Title</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#fff" }}>Listens</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#fff" }}>Private</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#fff" }}>Duration</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle2" align="center" sx={{ fontWeight: 700, color: "#fff" }}>
            Actions
          </Typography>
        </Grid>
      </Grid>
      <Box>
        {loadingMusic ? (
          <Typography color="#fff">Loading...</Typography>
        ) : myMusic.length === 0 ? (
          <Typography color="#fff">No music found.</Typography>
        ) : (
          myMusic.map((m, idx) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={m._id || idx}
              sx={{
                background: "#1a1a1a",
                borderRadius: "12px",
                mb: 3,
                px: 1,
                paddingTop: 1,
                paddingBottom: 2.5,
                "&:hover": { background: "#232323" },
              }}
            >
              <Grid item xs={4}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ cursor: "pointer" }}
                  onClick={() => dispatch(addSongsAction(myMusic, false, null, idx))}
                >
                  <Box
                    component="img"
                    src={m.thumbnailImg?.filePath || Images.circle_music}
                    alt={m.title}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    sx={{
                      height: 48,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2" color="#fff" fontWeight={700}>
                      {m.title}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="#ffea00"
                      fontWeight={600}
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={e => {
                        e.stopPropagation();
                        window.location.href = `/app/artistprofile/${m.artistData?.[0]?.uId || ""}`;
                      }}
                    >
                      {m.artistData && m.artistData.length
                        ? m.artistData.map(a => a.displayArtistName).join(", ")
                        : m.artist || ""}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Typography color="#ff8a3d" fontWeight={500}>
                  {m.listenCount ?? m.listens ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography color={m.private ? "#ff8a3d" : "#8fff8f"} fontWeight={500}>
                  {m.private ? "Yes" : "No"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography color="#ff8a3d" fontWeight={500}>
                  {HMS(m.duration)}
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "center", position: "relative" }}>
                <IconButton
                  aria-label="more"
                  onClick={() => {
                    setSelectedIndex(idx);
                    setOpen(idx);
                  }}
                  sx={{ color: "#fff" }}
                >
                  <MoreVertIcon />
                </IconButton>
                {selectedIndex === idx && open === idx && (
                  <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
                    <ProfileMenu
                      isLast={
                        idx === myMusic.length - 1 && myMusic.length > 2
                          ? "-90px !important"
                          : "40px"
                      }
                      right={"0px"}
                      isMenu={open === idx}
                      actions={getMenuActions(m)}
                      handleProfileMenuChoice={key => handleMenuAction(key, m._id, idx)}
                    />
                  </OutsideClickHandler>
                )}
              </Grid>
            </Grid>
          ))
        )}
      </Box>
      {/* Studio/Playlist popup */}
      <AddMusicToPlaylist
        musicId={studioMusicId}
        isAlbum={false}
        open={showStudioPopup}
        handleClose={() => setShowStudioPopup(false)}
      />
    </>
  );
}